import { CalibrationUnit, LengthUnitTypeEnum } from 'src/gql/graphql';
import { ActiveSheetData } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheet';
import { useCalibration } from 'src/components/viewers/SheetsViewerV2/hooks/useCalibration';
import { AggregatedView } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetsAggregatedView';
import { ApiCoordinatesConverter } from 'src/components/viewers/SheetsViewerV2/utils/apiCoordinatesConverter';

/** Imperial length units from project settings */
const IMPERIAL_LENGTH_UNITS = [
  LengthUnitTypeEnum.DecimalFeet,
  LengthUnitTypeEnum.DecimalInches,
  LengthUnitTypeEnum.FeetAndFractionalInches,
  LengthUnitTypeEnum.FractionalInches,
];

/**
 * Returns true if the project's length unit setting is imperial.
 * If no unit settings are configured, assumes metric.
 */
export function isLengthUnitImperial(
  lengthUnit: LengthUnitTypeEnum | undefined
): boolean {
  if (!lengthUnit) {
    return false;
  }
  return IMPERIAL_LENGTH_UNITS.includes(lengthUnit);
}

/**
 * Returns the calibration units available for the visual method based on project settings.
 * Metric (or no settings): meters, millimeters
 * Imperial: feet
 */
export function getCalibrationUnitOptions(
  lengthUnit: LengthUnitTypeEnum | undefined
): Array<{ value: CalibrationUnit; labelKey: string }> {
  if (isLengthUnitImperial(lengthUnit)) {
    return [
      {
        value: CalibrationUnit.Feet,
        labelKey: 'viewer-interactions.calibration.units.feet',
      },
    ];
  }
  return [
    {
      value: CalibrationUnit.Mm,
      labelKey: 'viewer-interactions.calibration.units.millimeters',
    },
    {
      value: CalibrationUnit.M,
      labelKey: 'viewer-interactions.calibration.units.meters',
    },
  ];
}

/**
 * Get the default calibration unit when no existing calibration.
 * Metric: meters. Imperial: feet.
 */
export function getDefaultCalibrationUnit(
  lengthUnit: LengthUnitTypeEnum | undefined
): CalibrationUnit {
  return isLengthUnitImperial(lengthUnit)
    ? CalibrationUnit.Feet
    : CalibrationUnit.M;
}

/**
 * Convert a value from one calibration unit to another.
 * All conversions go through meters as the canonical unit.
 */
export function convertCalibrationValue(
  value: number,
  fromUnit: CalibrationUnit,
  toUnit: CalibrationUnit
): number {
  if (fromUnit === toUnit) return value;

  // Convert to meters first
  let valueInMeters: number;
  switch (fromUnit) {
    case CalibrationUnit.Mm:
      valueInMeters = value / 1000;
      break;
    case CalibrationUnit.Feet:
      valueInMeters = value * 0.3048;
      break;
    case CalibrationUnit.M:
    default:
      valueInMeters = value;
      break;
  }

  // Convert from meters to target unit
  switch (toUnit) {
    case CalibrationUnit.Mm:
      return valueInMeters * 1000;
    case CalibrationUnit.Feet:
      return valueInMeters / 0.3048;
    case CalibrationUnit.M:
    default:
      return valueInMeters;
  }
}

export function getCalibrationDistance(
  viewer: AggregatedView['viewer'],
  activeSheetData: ActiveSheetData,
  calibration: ReturnType<typeof useCalibration>['existing']
) {
  if (!calibration) {
    return 0;
  }

  const converter = new ApiCoordinatesConverter(
    viewer,
    activeSheetData.viewport
  );
  const { pointA, pointB } = calibration;
  const convertedA = converter.convertToAutodeskPoint(pointA);
  const convertedB = converter.convertToAutodeskPoint(pointB);

  return convertedA.distanceTo(convertedB);
}
